import { DOCUMENT, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  input,
  InputSignal,
  OnInit,
  output,
  signal,
  Signal,
  WritableSignal
} from '@angular/core';

@Component({
  selector: 'oke-month-picker',
  imports: [NgTemplateOutlet],
  templateUrl: './month-picker.html',
  styleUrl: './month-picker.scss',
  host: {
    '[attr.required]': '$isRequired()',
    '[class.is-pilled]': '$isPilled()',
    '[class.is-waiting]': '$isWaiting()'
  }
})
export class MonthPicker implements OnInit, AfterViewInit {
  private readonly _todaysDate: Date = new Date();
  private readonly _hostElement = inject(ElementRef<HTMLElement>);
  private readonly _doc = inject(DOCUMENT);
  /**
   * @description optional input that determines whether this
   *   component should show a "required" asterisk
   * @type boolean
   * @default false
   */
  readonly $isRequired: InputSignal<boolean> = input<boolean>(false, {
    alias: 'show-is-required'
  });
  /**
   * @description optional input determines whether the
   *   Month Picker component has a border-radius to look
   *   more like a "pill" instead of the 90-degree borders
   * @type boolean
   * @default false
   */
  readonly $isPilled: InputSignal<boolean> = input<boolean>(false, {
    alias: 'is-pilled'
  });
  /**
   * @description optional input that sets this component's
   *   **initial** year & month. (Ensure this date is a valid
   *   JavaScript `Date` object and within your parameters.)
   * @type Date
   * @default null
   */
  readonly $initialMonth: InputSignal<Date | null> = input<Date | null>(null, {
    alias: 'initial-month'
  });
  /**
   * @description optional label for placement above the
   *   date fields. If unset, no label will appear.
   * @type string
   * @example "Accounting Month"
   * @default null
   */
  readonly $label: InputSignal<string | null> = input<string | null>(null, {
    alias: 'label'
  });
  /**
   * @description optional input determines how many months
   *   **backward** to make available for selection
   * @type number
   * @default 24
   */
  readonly $numMonthsBackward: InputSignal<number> = input<number>(24, {
    alias: 'num-months-backward'
  });
  /**
   * @description optional input determines how many months
   *   **forward** to make available for selection. _Note:_
   *   if set to zero, the most recent selectable month will
   *   be the **current** month. Otherwise, if it's left
   *   unset, the most recent selectable month will be the
   *   current month - 1.
   * @type number
   * @default -1
   */
  readonly $numMonthsForward: InputSignal<number> = input<number>(0, {
    alias: 'num-months-forward'
  });
  /**
   * @description Event fires—with a **Date** object—when
   *   a valid selection has been made. A valid selection
   *   requires both the _year_ and _month_ to be not null.
   *   The computed value is a Date object.
   * @type Date
   */
  protected readonly changed = output<Date>();
  private readonly _$isWaiting: WritableSignal<boolean> = signal<boolean>(false);
  protected readonly $isWaiting: Signal<boolean> = this._$isWaiting.asReadonly();
  private readonly _$selectedMonthIdx: WritableSignal<number | null> = signal<number | null>(null);
  private readonly _$selectedYear: WritableSignal<number | null> = signal<number | null>(null);
  protected readonly $selectedMonthIdx: Signal<number | null> = this._$selectedMonthIdx.asReadonly();
  protected readonly $selectedYear: Signal<number | null> = this._$selectedYear.asReadonly();
  protected readonly $allYears: Signal<number[]> = signal<number[]>(this._getAvailableYears()).asReadonly();

  ngOnInit(): void {
    if (this.$initialMonth()) {
      const initialYear = this.$initialMonth()!.getFullYear();
      const initialMonthIndex = this.$initialMonth()!.getMonth();
      this._$selectedMonthIdx.set(initialMonthIndex);
      this._$selectedYear.set(initialYear);
    } else {
      this._$selectedMonthIdx.set(null);
      this._$selectedYear.set(null);
    }
  }

  ngAfterViewInit(): void {
    if (!this.$initialMonth()) {
      return;
    }
    const htmlInputElement = this._doc.createElement('input');
    htmlInputElement.value = this.$initialMonth()!.getFullYear().toString();
    htmlInputElement.className = 'years';
    this.onDateFieldSelection({
      target: htmlInputElement
    });
  }

  reset(): void {
    this._$selectedMonthIdx.set(null);
    this._$selectedYear.set(null);
    ['years', 'months'].forEach(className => {
      this._getOptionElements(className as 'years' | 'months', true).forEach((optionElement, idx) => {
        optionElement.disabled = false;
        if (idx === 0) {
          optionElement.selected = true;
          optionElement.disabled = true;
        }
      });
    });
  }

  /**
   * @description "Setter" makes month picker element/component have
   *   a "waiting" state (whatever you want that to be via className)
   * @param isIt boolean
   * @returns void
   */
  set isWaiting(isIt: boolean) {
    this._$isWaiting.set(isIt);
  }

  /**
   * @description Component method handles the logic for determining
   *   which month(s) & year(s) are disabled when either a month or
   *   year is selected.
   * @param HTMLSelectElement
   * @returns void
   */
  protected onDateFieldSelection(changeEvt: Event | Record<'target', HTMLInputElement>): void {
    const selectElement = changeEvt.target as HTMLSelectElement;
    const selectedValue = Number(selectElement.value);
    const optionClassName: 'years' | 'months' = selectElement.className.includes('months') ? 'years' : 'months';
    if (optionClassName === 'years') {
      this._$selectedMonthIdx.set(selectedValue);
    } else if (optionClassName === 'months') {
      this._$selectedYear.set(selectedValue);
    }
    const onEachOptionElement = (optionElement: HTMLOptionElement): void => {
      const optionValue = Number(optionElement.value);
      const currentDate = new Date(
        this._todaysDate.getFullYear(),
        this._todaysDate.getMonth() - (this.$numMonthsForward() === -1 ? 1 : 0)
      );
      const optionDate = new Date(
        optionClassName === 'years' ? optionValue : selectedValue,
        optionClassName === 'years' ? selectedValue : optionValue
      );
      const isTooOld =
        Math.abs(this._getNumMonthsDifference(currentDate, optionDate)) >
        this.$numMonthsBackward() + (this.$numMonthsForward() === -1 ? -1 : 0);
      const isTooFuture =
        this._getNumMonthsDifference(currentDate, optionDate) >
        this.$numMonthsForward() + (this.$numMonthsForward() === -1 ? 1 : 0);
      let isDisabled = false;
      if (isTooOld || isTooFuture) {
        isDisabled = true;
      }
      optionElement.disabled = isDisabled;
    };
    if (optionClassName === 'months') {
      this._getOptionElements(optionClassName).forEach(onEachOptionElement);
    }
    this._broadcast();
  }

  /**
   * @description
   * @returns void
   */
  private _broadcast(): void {
    const isMonthValid = this.$selectedMonthIdx() !== null;
    const isYearValid = this.$selectedYear() !== null;
    if (isMonthValid && isYearValid) {
      this.changed.emit(new Date(this.$selectedYear() as number, this.$selectedMonthIdx() as number));
    }
  }

  /**
   * @description Utility method returns list of <option/> elements from
   *   <select/> identified by the selectClassName param
   * @param selectClassName string
   * @returns HTMLOptionElement[]
   */
  private _getOptionElements(selectClassName: 'months' | 'years', isForceGetAll = false): HTMLOptionElement[] {
    const optionElements = Array.from<HTMLOptionElement>(
      this._hostElement.nativeElement.querySelector(`.${selectClassName}`)?.children ?? []
    );
    if (!isForceGetAll) {
      return optionElements.filter((optionElement: HTMLOptionElement) => !isNaN(Number(optionElement.value)));
    }
    return optionElements;
  }

  /**
   * @description Utility method returns the number of months between
   *   two different dates
   * @param dateFrom Date
   * @param dateTo Date
   * @returns number
   */
  private _getNumMonthsDifference(dateFrom: Date, dateTo: Date): number {
    return dateTo.getMonth() - dateFrom.getMonth() + 12 * (dateTo.getFullYear() - dateFrom.getFullYear());
  }

  /**
   * @description Utility method returns an array of available
   *   full years, determined by the inputs of the host component
   * @returns number[]
   */
  private _getAvailableYears(): number[] {
    let availableYears: number[] = [];
    const todayYear = this._todaysDate.getFullYear();
    const todayMonthIndex = this._todaysDate.getMonth();
    const numMonthsBackward = this.$numMonthsBackward();
    const numMonthsForward = this.$numMonthsForward();
    if (numMonthsForward === -1) {
      const latestMonthIndex = todayMonthIndex !== 0 ? todayMonthIndex - 1 : 11;
      const numPreviousYears = Math.ceil((numMonthsBackward - latestMonthIndex) / 12);
      const numYears = numPreviousYears + (todayMonthIndex !== 0 ? 1 : 0);
      availableYears = Array.from(Array(numYears).keys()).map(i => todayYear - i - (todayMonthIndex === 0 ? 1 : 0));
    } else if (numMonthsForward === 0) {
      const latestMonthIndex = todayMonthIndex;
      const numPreviousYears = Math.ceil((numMonthsBackward - latestMonthIndex) / 12);
      const numYears = numPreviousYears + 1;
      availableYears = Array.from(Array(numYears).keys()).map(i => todayYear - i);
    } else if (numMonthsForward > 0) {
      const numPreviousYears =
        todayMonthIndex < numMonthsBackward ? Math.ceil((numMonthsBackward - todayMonthIndex) / 11) : 0;
      const numNextYears =
        11 - todayMonthIndex < numMonthsForward ? Math.ceil((numMonthsForward - (11 - todayMonthIndex)) / 12) : 0;
      availableYears = [todayYear];
      [numPreviousYears, numNextYears].forEach((numYearsValue, idx) => {
        Array.from(Array(numYearsValue).keys())
          .map(i => (idx === 0 ? todayYear - 1 - i : todayYear + 1 + i))
          .forEach(year => availableYears.push(year));
      });
    }
    return availableYears.sort((a, b) => b - a);
  }
}
